// One typed web API client. Every data fetch in apps/web routes through here so
// the four cross-cutting concerns — the /api base path, the X-Requested-With
// CSRF header, the 401→login redirect, and the { data, page? } envelope unwrap —
// live in exactly one place and cannot drift across call sites. See
// docs/remediation/audit-remediation-epic.md (Story 2, web-api-client) and the
// API contract in docs/decision/auth-google-sso.md / performance-budget.md.

import { MINIMAL_RETURN, RETURN_PREFERENCE_HEADER } from '@bbbk/schema';
import { signalMutation } from './mutationBus';

// --- The four single-source concerns ---------------------------------------

// 1. Base path. Call sites pass `/bookings`, `bookings`, or an already-built
//    `/api/...`; the client normalises to a single `/api/<path>`.
const API_BASE = '/api';

// 2. CSRF header. Defined once; every write request carries it.
export const CSRF_HEADER = 'X-Requested-With';
export const CSRF_VALUE = 'bbbk';

// Idempotency header (create-booking AC7). A write may carry a stable,
// client-generated key so a retried submit creates one booking, not two
// (docs/architecture/create-booking/api.md, ADR-5). The client owns the key
// because only it can tell a retry from a deliberate second create; the server
// treats it as opaque. Spelled here once so the call site and the server agree.
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

// RFC 7240 return preference. A write says whether it will actually READ the
// record the server sends back — and by default it will not.
//
// WHY THE DEFAULT IS "don't bother". Every write route rebuilds the full record
// to return it, and the Neon driver speaks HTTP, so each query inside that
// rebuild is its own round trip. A booking detail is eight of them, ~37ms each.
// Meanwhile NOT ONE call site in this app assigns the result of a write: they
// all `await api(...)` and discard it, taking freshness from the separate
// revalidating GET instead. So the app was paying ~300ms per write for an
// answer it always threw away.
//
// A caller that genuinely needs the record back passes `wantResult: true` and
// gets the old behaviour. Reads are unaffected — the header is only sent on
// write methods, where a "representation" is a thing the server had to build.
export { RETURN_PREFERENCE_HEADER, MINIMAL_RETURN } from '@bbbk/schema';

// 3. The login endpoint a 401 redirects to.
export const LOGIN_PATH = '/api/auth/login';
// The second IDP — personal Microsoft accounts (Hotmail/Outlook). The 401
// redirect still defaults to Google; this is the explicit "Sign in with
// Microsoft" button target. See docs/decision/auth-microsoft-sso.md.
export const MICROSOFT_LOGIN_PATH = '/api/auth/microsoft/login';

/** Join a path to the single API base. Idempotent for already-absolute /api paths. */
export function apiUrl(path: string): string {
  if (path.startsWith(API_BASE + '/') || path === API_BASE) return path;
  return API_BASE + (path.startsWith('/') ? path : '/' + path);
}

type WriteMethod = 'POST' | 'PATCH' | 'PUT' | 'DELETE';
type Method = 'GET' | WriteMethod;

const WRITE_METHODS: ReadonlySet<string> = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/**
 * Build the RequestInit for a call. Write methods carry the CSRF header; a
 * present body additionally carries the JSON content-type; a supplied
 * idempotency key adds the Idempotency-Key header (AC7). Reads carry none of
 * these (AC4) — an idempotencyKey passed to a GET is ignored. A supplied
 * `cache` (e.g. 'reload' on a write-revalidation refetch, read-cache-strategy
 * api.md) is threaded onto the RequestInit; omitted leaves the response
 * cacheable. Pure — no fetch, no side effects, so it is unit-testable.
 */
export function buildRequestInit(
  method: Method,
  body?: unknown,
  idempotencyKey?: string,
  cache?: RequestCache,
  wantResult?: boolean,
  form?: FormData,
): RequestInit {
  const init: RequestInit = { method };
  const isWrite = WRITE_METHODS.has(method);
  if (isWrite) {
    const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE };
    if (form !== undefined) {
      // Multipart — the card upload (customer-sign-in-registration AC26). NO
      // content-type header: the browser writes it, boundary included, and a
      // hand-set one would strip the boundary and leave the server a body it
      // cannot parse.
      init.body = form;
    } else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    if (idempotencyKey) headers[IDEMPOTENCY_HEADER] = idempotencyKey;
    // Ask the server not to build a record we are not going to read — see
    // RETURN_PREFERENCE_HEADER. Opt OUT with `wantResult`, not in, because
    // wanting it is the rare case here.
    if (!wantResult) headers[RETURN_PREFERENCE_HEADER] = MINIMAL_RETURN;
    init.headers = headers;
  }
  if (cache) init.cache = cache;
  return init;
}

// --- The shared envelope + error shapes ------------------------------------

// 4. Success envelope. `data` is always present; metadata (`page` for the
//    paged list, `scope` for the calendar window) varies by endpoint, so the
//    envelope is left open and the call site reads the key it needs.
export interface Envelope<T> {
  data: T;
  page?: { limit: number; offset: number; total: number | null; returned: number };
  scope?: { from: string; to: string; returned: number };
}

// The API error contract: { error: { code, message, field } }. A non-ok
// response is thrown as an ApiError so each call site keeps its own catch /
// error-state handling unchanged — the client centralises the shape, not the
// presentation.
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly field?: string | null;
  constructor(status: number, body?: { code?: string; message?: string; field?: string | null }) {
    super(body?.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code;
    this.field = body?.field;
  }
}

export interface ApiOptions {
  method?: Method;
  body?: unknown;
  /** A 401 redirects to LOGIN_PATH unless this is false (the /api/me case, AC5). */
  redirectOn401?: boolean;
  /** A stable, client-generated key making a retried write replay-safe (AC7). */
  idempotencyKey?: string;
  /**
   * The browser cache mode for this request. A post-write reload()/onSaved
   * refetch passes 'reload' so it bypasses any cached entry, reads through to
   * Neon, and replaces the stale copy (read-cache-strategy api.md, ADR-4).
   * Omitted on an ordinary read so the response stays cacheable.
   */
  cache?: RequestCache;
  /**
   * This write WILL read the record the server returns, so ask for it. Writes
   * default to `Prefer: return=minimal` because nothing in this app reads one —
   * see RETURN_PREFERENCE_HEADER. Set this and `data` is populated as before.
   */
  wantResult?: boolean;
  /** A multipart body — used instead of `body`. The card upload is the first. */
  form?: FormData;
  /**
   * A write no other read depends on — marking a notification read — signals nothing, so opening a
   * bubble does not revalidate every page behind it.
   */
  quiet?: boolean;
  /** Injectable seams for tests; default to the real fetch / window navigation. */
  fetchFn?: typeof fetch;
  redirect?: (to: string) => void;
}

const defaultRedirect = (to: string) => {
  window.location.href = to;
};

// The data domain a path belongs to (its first segment): '/account/users' →
// 'account', '/bookings/123' → 'bookings'. A write signals this tag so only the
// reads that depend on that domain revalidate (mutationBus).
export function domainTag(path: string): string {
  return /^\/?([^/?]+)/.exec(path)?.[1] ?? '';
}

/**
 * The one entry point. Routes the path through apiUrl, the init through
 * buildRequestInit, handles a 401 once (redirect by default), unwraps the
 * success envelope, and throws a typed ApiError on any other non-ok status.
 */
export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<Envelope<T>> {
  const {
    method = 'GET',
    body,
    redirectOn401 = true,
    idempotencyKey,
    cache,
    wantResult,
    form,
    quiet = false,
    fetchFn = fetch,
    redirect = defaultRedirect,
  } = opts;

  const r = await fetchFn(apiUrl(path), buildRequestInit(method, body, idempotencyKey, cache, wantResult, form));

  if (r.status === 401) {
    if (redirectOn401) redirect(LOGIN_PATH);
    throw new ApiError(401);
  }
  if (!r.ok) {
    let errBody: { code?: string; message?: string; field?: string | null } | undefined;
    try {
      errBody = ((await r.json()) as { error?: typeof errBody })?.error;
    } catch {
      /* non-JSON error body — status is still meaningful */
    }
    throw new ApiError(r.status, errBody);
  }

  // A successful write signals its domain tag (+ the wildcard), so the reads that
  // depend on this domain revalidate — scoped, not a blanket app-wide refetch.
  if (method !== 'GET' && !quiet) signalMutation([domainTag(path)]);

  return (await r.json()) as Envelope<T>;
}

// --- Un-enveloped probe ----------------------------------------------------

/**
 * The signed-in user's email, or null when signed out. `/api/me` returns a BARE
 * body (no { data } envelope), so it can't go through api<T>() — but routing it
 * here keeps the base path single-sourced and stops call sites hand-rolling a
 * fetch (AC2/AC5). A 401/!ok or a network error resolves to null (signed out),
 * never a redirect — matching App.tsx's /me probe, no redirect loop.
 */
export async function fetchMe(fetchFn: typeof fetch = fetch): Promise<{ email: string } | null> {
  try {
    const r = await fetchFn(apiUrl('/me'));
    if (!r.ok) return null;
    const m = (await r.json()) as { email?: string } | null;
    return m?.email ? { email: m.email } : null;
  } catch {
    return null;
  }
}

/**
 * End the session: POST /api/auth/logout (clears the KV session + the cookie).
 * Routed through the client so it carries the CSRF header like any write. The
 * caller decides where to go next (a full load to '/' re-probes /me → signed
 * out → the sign-in screen). The body is `{ ok: true }`, not enveloped, so it
 * is ignored.
 */
export async function logout(): Promise<void> {
  await api('/auth/logout', { method: 'POST' });
}
